import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { AddEditCostSplitForm } from 'components/templates/settlement/AddEditCostSplitModal/AddEditCostSplitForm';
import { AppModal, ErrorDisplay } from 'components/molecules';
import { settlementTexts } from 'consts/text';
import {
    ChildId,
    ChildSettlementId,
    CostAllocationFormFields,
    CostAllocationId,
    EditChildCostAllocation,
    ManualBillCostCategory,
} from 'types';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { useEffect } from 'react';

import { parseCostAllocationDTOToCostAllocationFormFieldsV2 } from 'utils/parsers/costAllocation/parseCostAllocationDTOToCostAllocationFormFields';
import { parseCostAllocationFormFieldsToAddCostAllocationDTO } from 'utils/parsers/costAllocation/parseCostAllocationFormFieldsToAddCostAllocationDTO';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    childId?: ChildId;
    costAllocationId?: CostAllocationId;
    type: 'monthly-settlement' | 'additional-costs';
    monthlySettlementId?: ChildSettlementId;
    additionalCostId?: string;
    correctionId?: string;
};

export const EditCostSplitModal = ({
    isOpen,
    onClose,
    childId,
    costAllocationId,
    type,
    monthlySettlementId,
    additionalCostId,
    correctionId,
}: Props) => {
    const { form, formInitialData, setFormData } = useEditForm<CostAllocationFormFields>();
    const { displaySuccess } = useSimpleNotification();

    const {
        data: costAllocationData,
        isFetching: isCostAllocationDataFetching,
        isError: isFetchCostAllocationDataError,
    } = useAppQuery(
        'COST_ALLOCATION',
        [costAllocationId],
        () => {
            return StaffHTTPService.settlements.getCostAllocation({
                costAllocationId: costAllocationId as CostAllocationId,
            });
        },
        {
            refetchOnWindowFocus: false,
        },
    );

    useEffect(() => {
        if (costAllocationData) {
            setFormData(parseCostAllocationDTOToCostAllocationFormFieldsV2(costAllocationData));
        }
    }, [costAllocationData, form, setFormData]);

    const onEditCostAllocationSuccess = () => {
        displaySuccess(settlementTexts.children.costAllocation.editSuccess);
        onClose();
    };

    const {
        mutate: editCostAllocation,
        error: editCostAllocationError,
        isLoading: isEditCostAllocationLoading,
    } = useAppMutation(StaffHTTPService.settlements.editCostAllocation, {
        key: ['EDIT_COST_ALLOCATION'],
        onSuccess: onEditCostAllocationSuccess,
        invalidateQueryKeys: [
            ['ADDITIONAL_COST_DETAILS'],
            ['ADDITIONAL_COST_LIST'],
            ['ADDITIONAL_COST'],
            ['COST_ALLOCATION', costAllocationId],
            ['CHILD_SETTLEMENT', childId],
            ['CHILD_SETTLEMENT_COST_SPLIT', childId],
        ],
    });

    const onEditCostAllocationFinish = (costAllocation: CostAllocationFormFields) => {
        editCostAllocation({
            costAllocation: parseCostAllocationFormFieldsToAddCostAllocationDTO({
                ...costAllocation,
                monthlySettlementId: monthlySettlementId ?? null,
                additionalCostId: additionalCostId ?? null,
                sourceType:
                    type === 'monthly-settlement' ? 'MONTHLY_SETTLEMENT' : 'ADDITIONAL_COST',
                costCategory: costAllocation.refersTo as ManualBillCostCategory,
                correctionId: correctionId ?? null,
            }) as EditChildCostAllocation,
            costAllocationId: costAllocationId as string,
        });
    };

    if (isFetchCostAllocationDataError) {
        return <ErrorDisplay />;
    }

    return (
        <AppModal
            width={700}
            open={isOpen}
            onCancel={onClose}
            isLoading={isEditCostAllocationLoading || isCostAllocationDataFetching}
        >
            <AddEditCostSplitForm
                type={type}
                mode="edit"
                form={form}
                axiosError={editCostAllocationError}
                isLoading={isEditCostAllocationLoading || isCostAllocationDataFetching}
                onFinish={onEditCostAllocationFinish}
                initialValues={formInitialData}
            />
        </AppModal>
    );
};
